import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────

export interface AppUser {
  id: string;
  organizationId: string;
  firebaseUid: string;
  email: string;
  fullName: string;
  /** Jackson serialises isActive() getter as "active" (strips "is" prefix) */
  active: boolean;
  language: string | null;
  avatarUrl: string | null;
  joinedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invitation {
  id: string;
  organizationId: string;
  email: string;
  targetKind: 'client_user' | 'supplier_user';
  targetTeamId: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  expired: boolean;
}

// ── Queries ───────────────────────────────────────────────────

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<AppUser[]>('/api/users'),
  });
}

export function usePendingInvitations() {
  return useQuery({
    queryKey: ['invitations', 'pending'],
    queryFn: () => api.get<Invitation[]>('/api/invitations?status=pending&kind=client_user'),
  });
}

// ── Mutations ─────────────────────────────────────────────────

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { email: string; teamId: string }) =>
      api.post<{ id: string }>('/api/invitations', {
        email: params.email,
        targetTeamId: params.teamId,
        targetKind: 'client_user',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post(`/api/users/${userId}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post(`/api/users/${userId}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.post(`/api/invitations/${invitationId}/resend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.post(`/api/invitations/${invitationId}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useUpdateOwnProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { fullName?: string; language?: string; avatarUrl?: string; phone?: string }) =>
      api.put('/api/auth/me', params),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}
